import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  whatsappRequestSignupOtp,
  whatsappVerifySignupOtp,
  whatsappLastSendEvent,
} from "@/lib/whatsapp.functions";
import { normalizeToE164, isValidE164 } from "@/lib/whatsapp/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";


export const Route = createFileRoute("/otp-test")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "OTP Test · TruWe" },
      { name: "robots", content: "noindex" },
    ],
  }),
  beforeLoad: () => {
    if (import.meta.env.PROD) {
      throw new Error("Not found");
    }
  },
  component: OtpTestPage,
});


type Step = "phone" | "verify";
type VerifyStatus =
  | { kind: "idle" }
  | { kind: "success"; authUserId?: string | null; email?: string | null }
  | { kind: "error"; error: string };

type ProviderEvent = {
  createdAt: string;
  status: string;
  template: string | null;
  errorCode: number | string | null;
  errorMessage: string | null;
  providerMessageId: string | null;
} | null;

function OtpTestPage() {
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>({ kind: "idle" });
  const [lastEvent, setLastEvent] = useState<ProviderEvent>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const requestOtp = useServerFn(whatsappRequestSignupOtp);
  const verifyOtp = useServerFn(whatsappVerifySignupOtp);
  const getLastEvent = useServerFn(whatsappLastSendEvent);

  const { e164, valid } = useMemo(() => {
    const n = normalizeToE164(phone);
    return { e164: n, valid: isValidE164(n) };
  }, [phone]);

  // Cooldown tick
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Autofocus code input when entering verify step
  useEffect(() => {
    if (step === "verify") codeInputRef.current?.focus();
  }, [step]);

  async function fetchLastEvent(target: string) {
    try {
      const r: any = await getLastEvent({ data: { phone: target } });
      if (r?.ok) setLastEvent(r.event);
    } catch {
      /* diagnostic only */
    }
  }

  async function sendOtp(e?: React.FormEvent) {
    e?.preventDefault();
    if (!valid || !e164) {
      toast.error("Enter a valid phone number");
      return;
    }
    setBusy(true);
    setVerifyStatus({ kind: "idle" });
    setLastEvent(null);
    try {
      const r = await requestOtp({ data: { phone: e164 } });
      // Always pull the latest send event so we can show Meta's exact error.
      await fetchLastEvent(e164);
      if (r.ok) {
        toast.success(`OTP sent to ${e164}`);
        setCooldown(r.cooldownSeconds ?? 45);
        setStep("verify");
        setCode("");
      } else {
        toast.error(`Failed: ${r.error}`);
        if (r.retryAfterSec) setCooldown(r.retryAfterSec);
      }
    } catch (err: any) {
      toast.error(err?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }


  async function verify(e?: React.FormEvent) {
    e?.preventDefault();
    if (!e164) return;
    if (!/^\d{4,8}$/.test(code)) {
      toast.error("Enter the numeric code from WhatsApp");
      return;
    }
    setBusy(true);
    try {
      const r: any = await verifyOtp({ data: { phone: e164, code } });
      if (r.ok) {
        toast.success("Phone verified ✓");
        setVerifyStatus({
          kind: "success",
          authUserId: r.authUserId ?? r.userId ?? null,
          email: r.email ?? null,
        });
      } else {
        const err = r.error || "verification_failed";
        toast.error(`Failed: ${err}`);
        setVerifyStatus({ kind: "error", error: err });
      }
    } catch (err: any) {
      const msg = err?.message || "Verification failed";
      toast.error(msg);
      setVerifyStatus({ kind: "error", error: msg });
    } finally {
      setBusy(false);
    }
  }

  const showPreview = phone.trim().length > 0;

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
      <h1 className="font-display text-3xl font-semibold text-brand-deep">
        WhatsApp OTP Test
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {step === "phone"
          ? "Enter a mobile number to send a real WhatsApp OTP via Meta."
          : `Enter the 6-digit code sent to ${e164}.`}
      </p>

      {step === "phone" && (
        <form onSubmit={sendOtp} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="phone">Mobile number</Label>
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              placeholder="9870645050 or +91 98706 45050"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              aria-invalid={showPreview && !valid}
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              10-digit numbers are treated as Indian (+91). For other countries, use E.164 (+CC…).
            </p>
            {showPreview && (
              <p className={`mt-1 text-xs ${valid ? "text-emerald-600" : "text-red-600"}`}>
                {valid ? `Will send to: ${e164}` : "Not a valid phone number"}
              </p>
            )}
          </div>
          <Button type="submit" className="h-11 w-full" disabled={busy || !valid}>
            {busy ? "Sending…" : "Send OTP"}
          </Button>
        </form>
      )}

      {step === "verify" && (
        <form onSubmit={verify} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="code">Verification code</Label>
            <Input
              id="code"
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{4,8}"
              maxLength={8}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="tracking-[0.4em] text-center text-lg"
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Check WhatsApp on {e164}. Codes expire after 5 minutes.
            </p>
          </div>

          <Button type="submit" className="h-11 w-full" disabled={busy || code.length < 4}>
            {busy ? "Verifying…" : "Verify code"}
          </Button>

          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => {
                setStep("phone");
                setCode("");
                setVerifyStatus({ kind: "idle" });
              }}
              className="text-muted-foreground underline-offset-2 hover:underline"
            >
              ← Change number
            </button>
            <button
              type="button"
              onClick={() => sendOtp()}
              disabled={busy || cooldown > 0}
              className="text-brand-deep underline-offset-2 hover:underline disabled:text-muted-foreground disabled:no-underline"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>

          {verifyStatus.kind === "success" && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-50 p-3 text-sm text-emerald-800">
              <div className="font-semibold">✅ Number authenticated</div>
              <div className="mt-1 text-xs">Phone: {e164}</div>
              {verifyStatus.email && (
                <div className="text-xs">Email: {verifyStatus.email}</div>
              )}
              {verifyStatus.authUserId && (
                <div className="text-xs">Auth user ID: {verifyStatus.authUserId}</div>
              )}
            </div>
          )}
          {verifyStatus.kind === "error" && (
            <div className="rounded-md border border-red-500/40 bg-red-50 p-3 text-sm text-red-800">
              <div className="font-semibold">❌ Not authenticated</div>
              <div className="mt-1 text-xs">Reason: {verifyStatus.error}</div>
            </div>
          )}
        </form>
      )}

      {lastEvent && (
        <div
          className={`mt-6 rounded-md border p-3 text-xs ${
            lastEvent.status === "sent"
              ? "border-emerald-500/40 bg-emerald-50 text-emerald-900"
              : "border-red-500/40 bg-red-50 text-red-900"
          }`}
        >
          <div className="font-semibold">
            Meta provider event · {lastEvent.status}
          </div>
          <div className="mt-1 opacity-80">
            {new Date(lastEvent.createdAt).toLocaleString()} · template{" "}
            {lastEvent.template ?? "-"}
          </div>
          {lastEvent.errorCode != null && (
            <div className="mt-1">Error code: {String(lastEvent.errorCode)}</div>
          )}
          {lastEvent.errorMessage && (
            <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px]">
              {lastEvent.errorMessage}
            </pre>
          )}
          {lastEvent.providerMessageId && (
            <div className="mt-1 opacity-70">
              Message ID: {lastEvent.providerMessageId}
            </div>
          )}
        </div>
      )}
    </main>
  );

}
